import { useState, useEffect } from 'react';
import { formatDate, getDateAfterDays } from '../../../../lib/date';
import request from '../../../../lib/request';

const OpenChat = () => {
  const [formData, setFormData] = useState({
    whatsappNumber: '',
    preferredDate: '',
    preferredTime: '',
    additionalNotes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [isPermitAllowed, setIsPermitAllowed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    checkIfPermitAllowed();
  }, []);

  const getChallenges = async () => {
    const currentChallenges = await request.get(
      `/user/challenges?status=CURRENT&localDate=${formatDate(new Date())}`
    );

    if (
      Array.isArray(currentChallenges.result) &&
      currentChallenges.result.length > 0
    ) {
      return true;
    }

    const historyChallenges = await request.get(
      `/user/challenges?status=HISTORY&localDate=${formatDate(new Date())}`
    );

    if (
      Array.isArray(historyChallenges.result) &&
      historyChallenges.result.length > 0
    ) {
      return true;
    }
  };

  const getMealList = async () => {
    const { result } = await request.get('/meal');
    return Array.isArray(result) && result.length > 0;
  };

  const checkIfRequested = async () => {
    const { result } = await request.get('/coach/open-chat/check');
    return result;
  };

  const checkIfPermitAllowed = async () => {
    try {
      const soft9DaysDone = await getChallenges();
      const mealDataDone = await getMealList();
      const requested = await checkIfRequested();

      setIsPermitAllowed(
        Boolean(soft9DaysDone) && Boolean(mealDataDone) && !requested
      );
    } catch (err) {
      console.log(err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.whatsappNumber ||
      !formData.preferredDate ||
      !formData.preferredTime
    ) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const dateTime = new Date(
        `${formData.preferredDate} ${formData.preferredTime.split('-')[0]}`
      ).toISOString();

      const { result } = await request.post('/coach/open-chat', {
        whatsappNumber: formData.whatsappNumber,
        additionalNotes: formData.additionalNotes,
        date: dateTime,
      });

      if (result) {
        setSubmitStatus('success');
        // Reset form after successful submission
        setFormData({
          whatsappNumber: '',
          preferredDate: '',
          preferredTime: '',
          additionalNotes: '',
        });

        setTimeout(() => {
          setSubmitStatus(null);
          setErrorMessage('');
        }, 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.log(err);
      if (err?.response?.data?.error === 'AlreadyRequested') {
        setErrorMessage(
          'You have already requested an open chat session. If you have any questions, please email us at support@thechamps.app'
        );
      } else {
        setErrorMessage(
          '✗ Failed to submit request. Please check your information and try again.'
        );
      }
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatWhatsAppNumber = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');

    // Format as WhatsApp number (add + if not present)
    if (cleaned.startsWith('0')) {
      return '+62' + cleaned.substring(1);
    } else if (!cleaned.startsWith('+') && cleaned.length > 0) {
      return '+' + cleaned;
    }

    return cleaned;
  };

  const handleWhatsAppChange = (e) => {
    const formatted = formatWhatsAppNumber(e.target.value);
    setFormData((prev) => ({
      ...prev,
      whatsappNumber: formatted,
    }));
  };

  return (
    <div className='min-h-screen bg-primary text-white p-4'>
      <div className='max-w-md mx-auto'>
        <div className='mb-8'>
          <h1 className='text-2xl font-bold mb-2'>
            Open Chat Request for Beginners
          </h1>
          <p className='text-gray-300 text-sm'>
            Request a personalized chat session with David if you are the first
            time user.
          </p>
          <br />
          <h2 className='text-lg font-bold mb-2 text-accent'>Requirements</h2>
          <ul className='text-gray-300 text-sm list-disc pl-4'>
            <li>At least 1 day of challenge completed</li>
            <li>At least 1 meal data</li>
            <li>Never requested an open chat session</li>
          </ul>
        </div>

        <div className='bg-secondary rounded-xl p-6'>
          <p className='text-gray-300 text-sm mb-6'>
            Fill out the form below to request an open chat session. We'll
            contact you on WhatsApp to confirm the details.
          </p>

          <form
            onSubmit={handleSubmit}
            className='space-y-4'
          >
            {/* WhatsApp Number */}
            <div>
              <label className='block text-sm font-medium text-white mb-2'>
                WhatsApp Phone Number *
              </label>
              <input
                type='tel'
                name='whatsappNumber'
                value={formData.whatsappNumber}
                onChange={handleWhatsAppChange}
                placeholder='+62 812 3456 7890'
                className='w-full bg-tertiary text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent placeholder-gray-400'
                required
              />
              <p className='text-xs text-gray-400 mt-1'>
                Include country code (e.g., +62 for Indonesia)
              </p>
              <p className='text-xs text-gray-400 mt-1'>
                Only numbers are allowed
              </p>
            </div>

            {/* Preferred Date */}
            <div>
              <label className='block text-sm font-medium text-white mb-2'>
                Preferred Date *
              </label>
              <input
                type='date'
                name='preferredDate'
                value={formData.preferredDate}
                onChange={handleInputChange}
                min={getDateAfterDays(new Date(), 1)}
                className='w-full bg-tertiary text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent'
                required
              />
            </div>

            {/* Preferred Time */}
            <div>
              <label className='block text-sm font-medium text-white mb-2'>
                Preferred Time *
              </label>
              <select
                name='preferredTime'
                value={formData.preferredTime}
                onChange={handleInputChange}
                className='w-full bg-tertiary text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent'
                required
              >
                <option value=''>Select a time slot</option>
                <option value='09:00-10:00'>09:00 - 10:00</option>
                <option value='10:00-11:00'>10:00 - 11:00</option>
                <option value='11:00-12:00'>11:00 - 12:00</option>
                <option value='13:00-14:00'>13:00 - 14:00</option>
                <option value='14:00-15:00'>14:00 - 15:00</option>
                <option value='15:00-16:00'>15:00 - 16:00</option>
                <option value='16:00-17:00'>16:00 - 17:00</option>
                <option value='17:00-18:00'>17:00 - 18:00</option>
                <option value='19:00-20:00'>19:00 - 20:00</option>
                <option value='20:00-21:00'>20:00 - 21:00</option>
              </select>
            </div>

            {/* Additional Notes */}
            <div>
              <label className='block text-sm font-medium text-white mb-2'>
                Additional Notes (Optional)
              </label>
              <textarea
                name='additionalNotes'
                value={formData.additionalNotes}
                onChange={handleInputChange}
                placeholder="Any specific topics you'd like to discuss or special requirements..."
                rows={3}
                className='w-full bg-tertiary text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent placeholder-gray-400 resize-none'
              />
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className='p-3 bg-green-500/20 border border-green-500/30 rounded-lg'>
                <p className='text-green-400 text-sm'>
                  ✓ Request submitted successfully! David will contact you via
                  email soon.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className='p-3 bg-red-500/20 border border-red-500/30 rounded-lg'>
                <p className='text-red-400 text-sm'>{errorMessage}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type='submit'
              disabled={
                isSubmitting || submitStatus === 'success' || !isPermitAllowed
              }
              className='w-full bg-accent hover:bg-accentHover disabled:bg-gray-600 disabled:cursor-not-allowed text-primary font-semibold py-3 px-4 rounded-lg transition-colors duration-200'
            >
              {isSubmitting ? (
                <span className='flex items-center justify-center gap-2'>
                  <div className='w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin'></div>
                  Submitting...
                </span>
              ) : submitStatus === 'success' ? (
                'Request Submitted!'
              ) : !isPermitAllowed ? (
                'Not Permitted'
              ) : (
                'Send Request'
              )}
            </button>
          </form>

          <div className='mt-6 p-4 bg-tertiary/50 rounded-lg'>
            <h3 className='text-sm font-medium text-white mb-2'>
              What to expect:
            </h3>
            <ul className='text-xs text-gray-300 space-y-1'>
              <li>
                • David will confirm your request within 24 hours via email
              </li>
              <li>• Chat session will be conducted via WhatsApp</li>
              <li>• Duration: 30-45 minutes</li>
              <li>• Free consultation for fitness and nutrition advice</li>
              <li className='text-red-500'>
                • If you have already requested an open chat session, you will
                not be able to request again
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenChat;
